import { ColorHandler, ColorIndex } from '@/utils/ColorHelper';
import { Egenskapsfilter } from '@/middleware/queries';

export const RECENT_CUSTOM_COLORS_KEY = 'recentCustomColors';
export const DEFAULT_VEGOBJEKTTYPE_COLORS_KEY = 'defaultVegobjekttypeColors';
export const DEFAULT_CATEGORY_COLORS_KEY = 'defaultCategoryColors';
export const MAX_RECENT_CUSTOM_COLORS = 5;

// Custom colors most recently picked by the user, across all road object types.
// Global (not per-type) and persisted in localStorage.
export function getRecentCustomColors(): string[] {
    try {
        const stored = JSON.parse(localStorage.getItem(RECENT_CUSTOM_COLORS_KEY) || '[]');
        return Array.isArray(stored) ? stored : [];
    } catch {
        return [];
    }
}

export function addRecentCustomColor(hex: string): string[] {
    const withoutDuplicate = getRecentCustomColors().filter(
        color => color.toLowerCase() !== hex.toLowerCase()
    );
    const updated = [hex, ...withoutDuplicate].slice(0, MAX_RECENT_CUSTOM_COLORS);
    localStorage.setItem(RECENT_CUSTOM_COLORS_KEY, JSON.stringify(updated));
    return updated;
}

type SavedColor = {
    mainColorIndex: number;
    categoryColorIndex: number;
    customColor: string | null;
};

const serializeColor = (color: ColorIndex): SavedColor => ({
    mainColorIndex: color.mainColorIndex,
    categoryColorIndex: color.categoryColorIndex,
    customColor: color.customColor,
});

const deserializeColor = (color: SavedColor): ColorIndex =>
    new ColorIndex(
        color.mainColorIndex,
        color.categoryColorIndex,
        color.customColor ?? null
    );

function getSavedDefaultColors(): Record<number, SavedColor> {
    try {
        const stored = JSON.parse(
            localStorage.getItem(DEFAULT_VEGOBJEKTTYPE_COLORS_KEY) || '{}'
        );
        return stored && typeof stored === 'object' ? stored : {};
    } catch {
        return {};
    }
}

// The persistent default color for a road object type, saved by the user. Applied whenever
// the type is added to the search again, including after a reload or on a shared link.
export function getDefaultVegobjekttypeColor(typeId: number): ColorIndex | null {
    const saved = getSavedDefaultColors()[typeId];
    if (!saved) return null;
    return deserializeColor(saved);
}

export function resolveInitialVegobjekttypeColor(
    typeId: number,
    colorHandler: ColorHandler
): ColorIndex {
    const savedDefaultColor = getDefaultVegobjekttypeColor(typeId);
    if (!savedDefaultColor) return colorHandler.getNextColorIndex();
    colorHandler.reserveColor(savedDefaultColor);
    return savedDefaultColor;
}

export function setDefaultVegobjekttypeColor(typeId: number, color: ColorIndex): void {
    const all = getSavedDefaultColors();
    all[typeId] = serializeColor(color);
    localStorage.setItem(DEFAULT_VEGOBJEKTTYPE_COLORS_KEY, JSON.stringify(all));
}

export function clearDefaultVegobjekttypeColor(typeId: number): void {
    const all = getSavedDefaultColors();
    delete all[typeId];
    localStorage.setItem(DEFAULT_VEGOBJEKTTYPE_COLORS_KEY, JSON.stringify(all));
}

type SavedCategoryColors = Record<number, Record<string, SavedColor>>;

export const categoryColorKey = (filters: Egenskapsfilter[]): string =>
    JSON.stringify(
        filters.map(filter => [
            filter.egenskapstype.id,
            filter.operator,
            filter.verdiToString(),
        ])
    );

function getSavedCategoryColors(): SavedCategoryColors {
    try {
        const stored = JSON.parse(
            localStorage.getItem(DEFAULT_CATEGORY_COLORS_KEY) || '{}'
        );
        return stored && typeof stored === 'object' ? stored : {};
    } catch {
        return {};
    }
}

export function getDefaultCategoryColor(
    typeId: number,
    filters: Egenskapsfilter[]
): ColorIndex | null {
    const saved = getSavedCategoryColors()[typeId]?.[categoryColorKey(filters)];
    return saved ? deserializeColor(saved) : null;
}

export function hasDefaultCategoryColors(typeId: number): boolean {
    return Object.keys(getSavedCategoryColors()[typeId] ?? {}).length > 0;
}

export function resolveInitialCategoryColor(
    typeId: number,
    filters: Egenskapsfilter[],
    colorHandler: ColorHandler
): ColorIndex {
    const savedDefaultColor = getDefaultCategoryColor(typeId, filters);
    if (!savedDefaultColor) return colorHandler.getNextColorIndex();
    colorHandler.reserveColor(savedDefaultColor);
    return savedDefaultColor;
}

export function setDefaultCategoryColor(
    typeId: number,
    filters: Egenskapsfilter[],
    color: ColorIndex
): void {
    const all = getSavedCategoryColors();
    all[typeId] = { ...all[typeId], [categoryColorKey(filters)]: serializeColor(color) };
    localStorage.setItem(DEFAULT_CATEGORY_COLORS_KEY, JSON.stringify(all));
}

export function clearDefaultCategoryColor(
    typeId: number,
    filters: Egenskapsfilter[]
): void {
    const all = getSavedCategoryColors();
    if (!all[typeId]) return;
    delete all[typeId][categoryColorKey(filters)];
    if (Object.keys(all[typeId]).length === 0) delete all[typeId];
    localStorage.setItem(DEFAULT_CATEGORY_COLORS_KEY, JSON.stringify(all));
}

export function clearDefaultCategoryColors(typeId: number): void {
    const all = getSavedCategoryColors();
    delete all[typeId];
    localStorage.setItem(DEFAULT_CATEGORY_COLORS_KEY, JSON.stringify(all));
}
